import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { apiClient, wsUrl } from "@/lib/api";
import { KonattuLogo } from "@/lib/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import DrawTab from "@/admin/DrawTab";
import RegistrationsTab from "@/admin/RegistrationsTab";
import SummaryTab from "@/admin/SummaryTab";
import QRCodesTab from "@/admin/QRCodesTab";

const TABS = ["draw", "registrations", "summary", "qr"];

export default function AdminPage() {
  const [pin, setPin] = useState("");
  const [authed, setAuthed] = useState(false);
  const [tab, setTab] = useState("draw");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const ok = sessionStorage.getItem("kg_admin") === "1";
    if (ok) setAuthed(true);
  }, []);

  const submitPin = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await apiClient.post("/admin/login", { pin });
      sessionStorage.setItem("kg_admin", "1");
      setAuthed(true);
      toast.success("Welcome, Admin");
    } catch {
      toast.error("Incorrect PIN");
    } finally {
      setSubmitting(false);
    }
  };

  if (!authed) {
    return (
      <div className="min-h-screen flex items-center justify-center px-5 bg-white">
        <form
          onSubmit={submitPin}
          className="kg-card kg-card-red-top p-8 w-full max-w-sm"
          data-testid="admin-pin-form"
        >
          <div className="flex justify-center mb-6">
            <KonattuLogo className="h-14" showTagline={false} />
          </div>
          <h2 className="font-display text-2xl text-center text-neutral-900">Admin Access</h2>
          <p className="text-center text-neutral-500 text-sm mt-1">Enter your 4-digit PIN</p>
          <Input
            type="password"
            inputMode="numeric"
            value={pin}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 8))}
            placeholder="••••"
            className="kg-input mt-6 h-14 text-center text-2xl tracking-[0.6em] border-2"
            data-testid="admin-pin-input"
          />
          <Button
            type="submit"
            disabled={submitting || pin.length < 4}
            className="kg-btn-red w-full h-12 mt-5"
            data-testid="admin-pin-submit"
          >
            {submitting ? "Verifying..." : "Enter"}
          </Button>
          <div className="mt-6 text-center">
            <Link to="/" className="text-xs text-neutral-500">← Back to home</Link>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <div className="border-b border-neutral-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <KonattuLogo className="h-10" showTagline={false} />
            <span className="kg-badge-red" data-testid="admin-badge">Admin Panel</span>
          </div>
          <div className="flex items-center gap-4 text-sm">
            <Link to="/history" className="text-neutral-700 hover:text-[#CC0000]" data-testid="history-link">Draw History</Link>
            <button
              onClick={() => { sessionStorage.removeItem("kg_admin"); setAuthed(false); }}
              className="text-neutral-500 hover:text-[#CC0000]"
              data-testid="admin-logout"
            >Logout</button>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-6 flex gap-2 overflow-x-auto">
          {[
            { id: "draw", label: "Draw Winners" },
            { id: "registrations", label: "Registrations" },
            { id: "summary", label: "Summary" },
            { id: "qr", label: "QR Codes" },
          ].map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`px-4 py-3 text-sm font-semibold whitespace-nowrap ${tab === t.id ? "kg-tab-active" : "text-neutral-600 hover:text-neutral-900"}`}
              data-testid={`tab-${t.id}`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {tab === "draw" && <DrawTab />}
        {tab === "registrations" && <RegistrationsTab />}
        {tab === "summary" && <SummaryTab />}
        {tab === "qr" && <QRCodesTab />}
      </div>
    </div>
  );
}
